import ExportArtifactOption from "../ExportArtifactOption";
import useExportSetupArtifact from "../useExportSetupArtifact";
import { styled } from "@linaria/react";
import React, { useMemo } from "react";
import { useDelete, usePost } from "../../../../../api/useCall";
import { bufferToBase64 } from "../../../../../lib/bufferConverter";
import useInput from "@ui/lib/hooks/useInput";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../../i18n";
import { color, fonts } from "@ui";
import Dropzone from "@ui/Dropzone";
import ImageReader from "@ui/ImageReader";
import ModalForm from "@ui/Modal/Form";
import Select from "@ui/Select";
import { useQuantities } from "../../../../../api/useQuantities";
import type { FileKeys, MapFile, TypeModel } from "./BatchModelImport/FilesType";
import {
  FileType,
  getImageModelParsingErrorMessage,
  isModelType,
} from "./BatchModelImport/FilesType";
import { modelTypes } from "./BatchModelImport/ImportModelModalForm";
import ModalInfos from "./BatchModelImport/ModalInfos";
import validateImport from "./BatchModelImport/validateImport";
import { HeaderWrapper, WarningComponent } from "./SimulationStyle";
import type { Project } from "../../../../../api/useProjects";
import useQuery from "../../../../../api/useQuery";
import { useSetups, type Setup } from "../../../../../api/useSetups";
import { useCanUserDoOnProject } from "../../../../canUserDo";
import BlankStateV2 from "@ui/BlankStateV2";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Text, { H3 } from "@ui/Text";
import DropzoneV2 from "@ui/DropzoneV2";
import type { FILE_PARSING_ERROR } from "../../../../../domain/parser/common/file";
import { MAX_SIZE_MODEL_IMAGE, parseFile } from "../../../../../domain/parser/common/file";
import { isResultErr } from "../../../../../lib/patterns/result";
import type { ParsingError } from "../../../../../domain/parser/types";
import useActiveAnalysesStream, {
  useSetupActiveAnalysisCount,
} from "../../../../../api/useActiveAnalysesStream";
import { DeleteBinLine, DownloadLine, More2Fill, UploadLine } from "@ui/IconV2/react";
import Menu from "@ui/Menu";
import Option, { OptionGroup } from "@ui/Option";
import ButtonIconV2 from "@ui/ButtonIconV2";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { css } from "@linaria/core";
import Card from "@ui/Card";
import { useSettings } from "../../../../../api/useSettings";
import { useImpactedAnalyses } from "../useAnalysisImpacted";
import { getCorpusGraph } from "diagnostics-lib/runtime";

const StyledBlankStateV2 = styled(BlankStateV2)`
  padding: 25px 20px;
`;

const FileNameContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const ModelDetails = styled.div`
  font: ${fonts.body1};
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const MenuButton = css`
  width: 32px;
  padding: 0;
  height: 32px;
`;

const ModelImageEmptyCard = css`
  height: auto;
`;

const useImageHash = (projectId: string, imageHash: string | null) =>
  useQuery("/projects/:projectId(uuid)/model-image/:imageHash", {
    routeParams: {
      projectId,
      imageHash: imageHash === null ? "not-yet" : imageHash,
    },
    disabled: imageHash === null,
  })?.body.content ?? null;

export const ModelInfo = React.memo(
  ({
    projectId,
    setup,
    openModal,
  }: {
    projectId: string;
    setup: Setup;
    openModal: (name: string) => void;
  }) => {
    const canUserEditSetup = useCanUserDoOnProject("PROJECT_SETUP", projectId);
    const { exportArtifact: exportImage, loading: exportingImage } = useExportSetupArtifact(
      projectId,
      setup.id,
      "image",
    );
    const modelImage = useImageHash(projectId, setup.simulationModel?.imageFileHash ?? null);

    return (
      <section>
        <HeaderWrapper>
          <H3>{t("setup.paragraphs.model")}</H3>
          {setup.simulationModel !== null && canUserEditSetup && (
            <Menu
              arrowWidth={0}
              //   gap={16}
              placement="bottom-end"
              overlay={
                <>
                  <OptionGroup>
                    <ExportArtifactOption
                      projectId={projectId}
                      setupId={setup.id}
                      section="model"
                    />
                    <Option closeOnClick onClick={() => openModal("replaceModel")}>
                      <>
                        <UploadLine color={color.carbon800} />
                        {t("commons.message_card.replace")}
                      </>
                    </Option>
                    <Option closeOnClick onClick={() => openModal("deleteModel")}>
                      <DeleteBinLine color={color.carbon800} />
                      {t("commons.message_card.delete")}
                    </Option>
                  </OptionGroup>
                </>
              }
            >
              <ButtonIconV2 className={MenuButton}>
                <More2Fill size="m" />
              </ButtonIconV2>
            </Menu>
          )}
        </HeaderWrapper>
        {setup.simulationModel === null ? (
          <Card className={ModelImageEmptyCard}>
            <StyledBlankStateV2
              title={t("setup.paragraphs.model.blank_state.title")}
              subtitle={canUserEditSetup ? t("setup.paragraphs.model.blank_state.text") : null}
              onClick={canUserEditSetup ? () => openModal("importModel") : undefined}
              buttonLabel={
                canUserEditSetup ? t("setup.paragraphs.model.blank_state.button") : undefined
              }
            />
          </Card>
        ) : (
          <>
            <ModelDetails>
              <p>
                <Text as="strong" type="body1SemiBold">
                  {t("setup.paragraphs.model.info.label.type")}
                </Text>
                {modelTypes.find(m => m.value === setup.simulationModel!.type)?.label}
              </p>
              <p>
                <Text as="strong" type="body1SemiBold">
                  {t("setup.paragraphs.model.info.label.files")}{" "}
                </Text>
                {setup.simulationModel.mainFile.name}
                {setup.simulationModel.variablesFile && (
                  <>, {setup.simulationModel.variablesFile.name}</>
                )}
                {setup.simulationModel.parametersFile && (
                  <>, {setup.simulationModel.parametersFile.name}</>
                )}
              </p>
              <p>
                <Text as="strong" type="body1SemiBold">
                  {t("setup.paragraphs.model.info.label.image")}{" "}
                </Text>
                <FileNameContainer>
                  {setup.simulationModel.imageFile?.name ??
                    t("setup.paragraphs.model.synoptic.no_model.title.short")}
                  {canUserEditSetup && setup.simulationModel.imageFile !== null && (
                    <span>
                      <TooltipV2
                        overlay={<OverlayWrapper text={t("commons.message_card.export")} />}
                      >
                        <ButtonIconV2
                          onClick={exportImage}
                          disabled={exportingImage}
                          aria-label={t("commons.message_card.export")}
                        >
                          <DownloadLine size="s" />
                        </ButtonIconV2>
                      </TooltipV2>
                      <TooltipV2 overlay={<OverlayWrapper text={t("common.file.import")} />}>
                        <ButtonIconV2
                          onClick={() => openModal("importImage")}
                          aria-label={t("common.file.import")}
                        >
                          <UploadLine size="s" />
                        </ButtonIconV2>
                      </TooltipV2>
                      <TooltipV2
                        overlay={<OverlayWrapper text={t("commons.message_card.delete")} />}
                      >
                        <ButtonIconV2
                          onClick={() => openModal("deleteImage")}
                          aria-label={t("commons.message_card.delete")}
                        >
                          <DeleteBinLine size="s" />
                        </ButtonIconV2>
                      </TooltipV2>
                    </span>
                  )}
                </FileNameContainer>
              </p>
            </ModelDetails>
            {modelImage ? (
              <ImageReader data={bufferToBase64(modelImage)} />
            ) : (
              <Card className={ModelImageEmptyCard}>
                <StyledBlankStateV2
                  title={t("setup.paragraphs.model.synoptic.no_model.title")}
                  subtitle={
                    canUserEditSetup
                      ? t("setup.paragraphs.model.synoptic.no_model.upload_a_model.text")
                      : null
                  }
                  onClick={canUserEditSetup ? () => openModal("importImage") : undefined}
                  buttonLabel={canUserEditSetup ? t("setup.simulation.import_image") : undefined}
                  //   picto={<Synoptic />}
                />
              </Card>
            )}
          </>
        )}
      </section>
    );
  },
);

const analysisStatus = new Set([
  "SIMULATION",
  "SIMULATION_ERROR",
  "DIAGNOSIS",
  "DIAGNOSIS_ERROR",
] as const);

export const DeleteModelModal = ({
  onRequestClose,
  project,
  setup,
}: {
  onRequestClose: () => void;
  project: Project;
  setup: Setup;
}) => {
  const projectId = project.id;
  const setupId = setup.id;
  const deleteEndpoint = useDelete(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/model",
  );
  const { activeAnalyses } = useActiveAnalysesStream(projectId);

  const impactedAnalyses = useImpactedAnalyses({
    setups: useSetups(projectId),
    activeAnalyses,
    setupId,
    analysisStatus,
  });
  const showAdditionalMessage = setup.simulationStatus === "VALID" && impactedAnalyses.length > 0;

  const deleteModel = React.useCallback(async () => {
    await deleteEndpoint({ routeParams: { projectId, setupId } });
    return true;
  }, [deleteEndpoint, projectId, setupId]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={deleteModel}
      okButtonName={t("modal.delete")}
      okButtonVariant="destructive"
    >
      <Text>{t("setup.paragraphs.model.modal.delete_model.text")}</Text>
      {showAdditionalMessage ? (
        <AdditionnalMessage
          status="WARNING"
          text={t("setup.modal.delete.analysis.additionnal_message.warning", {
            simulationValid: setup.simulationStatus === "VALID",
            diagnosisValid: setup.diagnosisStatus === "COMPLETE",
            impactedAnalysisCount: impactedAnalyses.length,
          })}
        />
      ) : null}
    </ModalForm>
  );
};

export const DeleteModelImageModal = ({
  onRequestClose,
  project,
  setup,
}: {
  onRequestClose: () => void;
  project: Project;
  setup: Setup;
}) => {
  const projectId = project.id;
  const setupId = setup.id;
  const deleteImageModel = useDelete(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/model/image",
  );
  const { addToast } = useToasts();

  const deleteImage = React.useCallback(async () => {
    await deleteImageModel({ routeParams: { projectId, setupId } });
    addToast("success", t("setup.paragraphs.model.modal.delete_image.toast.success"));
    return true;
  }, [deleteImageModel, projectId, setupId, addToast]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={deleteImage}
      okButtonName={t("modal.delete")}
      okButtonVariant="destructive"
    >
      <div>{t("setup.paragraphs.model.modal.delete_image.text")}</div>
    </ModalForm>
  );
};

export const ImportModelImage = ({
  setup,
  project,
  onRequestClose,
}: {
  onRequestClose: () => void;
  setup: Setup;
  project: Project;
}) => {
  const projectId = project.id;
  const setupId = setup.id;
  const { addToast } = useToasts();
  const importImageModel = usePost(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/model/image",
  );

  /**
   * Handle file upload
   */
  const [file, setFile] = React.useState<File | null>(null);
  const [isValid, setIsValid] = React.useState<boolean>();
  const [errorText, setErrorText] = React.useState<string>();
  const [errorDetails, setErrorDetails] = React.useState<ParsingError<FILE_PARSING_ERROR>>();

  const handleSubmit = async () => {
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      await importImageModel({
        routeParams: { projectId, setupId },
        body: formData,
      });
      addToast("success", t("setup.paragraphs.model.modal.update_image.toast.success"));
    }
    onRequestClose();
    return true;
  };

  const downloadErrorDetails = React.useCallback(() => {
    const errors = JSON.stringify(errorDetails, null, 4);
    const a = document.createElement("a");
    a.setAttribute("href", URL.createObjectURL(new Blob([errors], { type: "application/json" })));
    a.setAttribute("download", `${new Date().toISOString()}_image_model_import_errors.json`);
    a.click();
  }, [errorDetails]);

  const handleDropImage = React.useCallback(async ([file]: File[]) => {
    if (!file) return;

    setFile(file);

    /**
     * Handle file error
     */
    const fileResult = await parseFile(file, { expectedMimeType: ["image/svg+xml"] });
    if (isResultErr(fileResult) && fileResult.value) {
      setIsValid(false);
      setErrorText(getImageModelParsingErrorMessage(fileResult.value));
      setErrorDetails(fileResult.value);
      return;
    }

    /**
     * Parsing was successful.
     */
    setIsValid(true);
  }, []);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={isValid ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.import")}
    >
      <DropzoneV2
        accept={[".svg"]}
        onDrop={handleDropImage}
        multiple={false}
        nameFile={file?.name}
        validation={isValid}
        errorText={isValid ? undefined : errorText}
        downloadErrors={downloadErrorDetails}
        maxSize={MAX_SIZE_MODEL_IMAGE}
      />
    </ModalForm>
  );
};

interface ImportModelModalProps {
  onRequestClose: () => void;
  setup: Setup;
  project: Project;
  displayWarning?: boolean;
}
export const ImportModelModal = ({
  setup,
  project,
  onRequestClose,
  displayWarning,
}: ImportModelModalProps) => {
  const projectId = project.id;
  const setupId = setup.id;
  const activeAnalysisCount = useSetupActiveAnalysisCount(projectId, setupId);

  const settings = useSettings();
  const modelType = useInput(FileType.fmuTf as TypeModel);
  const modelTypesAllowed = React.useMemo(() => {
    if (settings.ALLOW_USE_DYMOSIM_AND_FMU_T0) {
      return modelTypes;
    }
    return modelTypes.filter(model => model.value === FileType.fmuTf);
  }, [settings]);

  const [submitted, setSubmitted] = React.useState(false);
  const [valid, setValid] = React.useState<boolean>(false);
  const importEndpoint = usePost(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/model",
  );
  const quantities = useQuantities(projectId);
  const quantityNames = quantities.map((quantity: { name: string }) => quantity.name);
  const { addToast } = useToasts();

  const [uploadedFiles, setUploadedFiles] = React.useState<{
    files: File[];
    validations: Map<FileKeys, MapFile>;
  }>({
    files: [],
    validations: new Map(),
  });

  const resetState = React.useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      modelType.onChange(event);
      setUploadedFiles({
        files: [],
        validations: new Map(),
      });
      setSubmitted(false);
    },
    [modelType],
  );

  const corpus = useMemo(
    () =>
      quantities.reduce<Record<string, string | null>>(
        (acc, { name, formula }) => ((acc[name] = formula), acc),
        {},
      ),
    [quantities],
  );
  const graph = useMemo(() => {
    let valid, graph;
    try {
      ({ graph, valid } = getCorpusGraph(corpus));
    } catch (e) {
      return null;
    }
    return valid ? (graph as { [token: string]: string[] | null }) : null;
  }, [corpus]);

  const expressionEngineInput: {
    corpus: Record<string, string | null>;
    graph: {
      [token: string]: string[] | null;
    } | null;
  } = useMemo(() => ({ corpus, graph }), [corpus, graph]);

  const handleDrop = React.useCallback(
    async (files: File[]) => {
      setUploadedFiles({ ...uploadedFiles, ...{ files } });

      const validations = await validateImport(
        files,
        quantityNames,
        modelType.value,
        expressionEngineInput,
      );
      const { value: modelTypeValue } = modelType;
      // all required value are set with no error
      const isFileOk = (fileType: FileKeys) =>
        validations.get(fileType)?.fileName !== undefined &&
        validations.get(fileType)?.error === undefined;

      let requiredFilesOk;
      if (modelTypeValue === FileType.dymola) {
        requiredFilesOk = isFileOk("main") && isFileOk("variables");
      } else if (modelTypeValue === FileType.fmuTf || modelTypeValue === FileType.fmuTo) {
        requiredFilesOk = isFileOk("main");
      } else {
        throw new Error(`Invalid model type: ${modelTypeValue}`);
      }

      setValid(requiredFilesOk);
      setSubmitted(true);
      setUploadedFiles({ ...uploadedFiles, ...{ files, validations } });
    },
    [modelType, quantityNames, uploadedFiles, expressionEngineInput],
  );

  const handleSubmit = React.useCallback(async () => {
    const modelData = new FormData();
    const mainFile = uploadedFiles.files.find(
      f => f.name === uploadedFiles.validations.get("main")?.fileName[0],
    );
    const variablesFile = uploadedFiles.files.find(
      f => f.name === uploadedFiles.validations.get("variables")?.fileName[0],
    );
    const imageFile = uploadedFiles.files.find(
      f => f.name === uploadedFiles.validations.get("image")?.fileName[0],
    );
    const parametersFile = uploadedFiles.files.find(
      f => f.name === uploadedFiles.validations.get("parameters")?.fileName[0],
    );

    if (mainFile === undefined) throw new Error("should not happen");

    modelData.append("main", mainFile);

    if (variablesFile) modelData.append("variables", variablesFile);
    if (parametersFile) modelData.append("parameters", parametersFile);
    if (imageFile) modelData.append("image", imageFile);

    if (isModelType(modelType.value)) {
      await importEndpoint({
        routeParams: { projectId, setupId },
        query: { type: modelType.value },
        body: modelData,
      });

      addToast("success", t("setup.simulation.import_batch.checking"));
      return true;
    } else {
      throw new Error("should not happen");
    }
  }, [
    importEndpoint,
    modelType.value,
    projectId,
    setupId,
    uploadedFiles.files,
    uploadedFiles.validations,
    addToast,
  ]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={submitted && valid ? handleSubmit : null}
      okButtonName={t("modalForm.button.name.import")}
    >
      <Select
        autoFocus
        options={modelTypesAllowed}
        value={modelType.value}
        onChange={resetState}
        label={t("setup.simulation.import_model.modal.subtitle")}
        prompt={t("setup.paragraphs.model.actions.import.modal.select.prompt")}
        clearable={false}
        disabled={settings.ALLOW_USE_DYMOSIM_AND_FMU_T0 === false}
      />
      {/* NOTE: We can't specify accept mime-type, as dymosim executable file is unknown */}
      <Dropzone
        onDrop={handleDrop}
        multiple
        // accept={[
        //   ".fmu",
        //   "application/octet-stream",
        //   "text/plain",
        //   "text/csv",
        //   "image/svg+xml",
        //   "image/png",
        // ]}
      />
      <ModalInfos
        modelType={modelType.value}
        submitted={submitted}
        validation={uploadedFiles.validations}
        invalid={submitted && !valid}
        sectionsDisplayed={["main", "variables", "parameters", "image"]}
      />
      {displayWarning && <WarningComponent impactedAnalysisCount={activeAnalysisCount} />}
    </ModalForm>
  );
};
